/* Form Review / Print screen — prefilled fields overlaid on the template image. */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { isDealerUser } from "../lib/roles";
import { authBlobUrl } from "../lib/dealJacket";

export default function AdminGeneratedFormView() {
  const { id: customerId } = useParams();
  const [params] = useSearchParams();
  const templateId = params.get("template_id");
  const generatedIdParam = params.get("generated_id");
  const { user, ready } = useAuth();
  const navigate = useNavigate();

  const [template, setTemplate] = useState(null);
  const [values, setValues] = useState({});
  const [missing, setMissing] = useState([]);
  const [requiredMissing, setRequiredMissing] = useState([]);
  const [generatedId, setGeneratedId] = useState(generatedIdParam || null);
  const [generated, setGenerated] = useState(null);
  const [pageUrls, setPageUrls] = useState({}); // {page: blobUrl}
  const [saving, setSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const printRootRef = useRef(null);

  useEffect(() => { if (ready && !isDealerUser(user)) navigate("/dealer-login"); }, [ready, user, navigate]);

  const load = useCallback(async () => {
    if (generatedIdParam) {
      // Load saved version
      const dj = await api.get(`/admin/crm/customer/${customerId}/deal-jacket`);
      const g = (dj.data.generated || []).find(x => x.id === generatedIdParam);
      if (!g) { alert("Saved document not found"); navigate(`/admin/crm/customer/${customerId}`); return; }
      const t = await api.get(`/admin/documents/templates/${g.template_id}`);
      setTemplate(t.data.template);
      setValues(g.field_values || {});
      setGenerated(g);
      setGeneratedId(g.id);
      // Recompute missing
      const m = []; const rm = [];
      (t.data.template.fields || []).forEach(f => {
        const v = (g.field_values || {})[f.id];
        if (v === undefined || v === null || v === "") {
          const entry = { id: f.id, label: f.label, required: !!f.required };
          m.push(entry); if (f.required) rm.push(entry);
        }
      });
      setMissing(m); setRequiredMissing(rm);
    } else {
      // Fresh generate (no save yet — caller should have hit /generate already to get values)
      const r = await api.post(`/admin/crm/customer/${customerId}/deal-jacket/generate`,
        { template_id: templateId, save: false });
      setTemplate(r.data.template);
      setValues(r.data.field_values || {});
      setMissing(r.data.missing || []);
      setRequiredMissing(r.data.required_missing || []);
    }
  }, [customerId, templateId, generatedIdParam, navigate]);
  useEffect(() => { if (isDealerUser(user) && templateId) load(); }, [user, templateId, load]);

  // Load page images
  useEffect(() => {
    if (!template) return;
    let revokes = [];
    Promise.all((template.pages || []).map(p => authBlobUrl(p.url).then(u => [p.page, u])))
      .then(arr => {
        const map = {};
        arr.forEach(([k, u]) => { if (u) { map[k] = u; revokes.push(u); } });
        setPageUrls(map);
      });
    return () => { revokes.forEach(u => URL.revokeObjectURL(u)); };
  }, [template]);

  const setValue = (fieldId, value) => setValues(v => ({ ...v, [fieldId]: value }));

  const save = async (updateCustomer) => {
    if (!template) return;
    setSaving(true); setStatusMsg("");
    try {
      let gid = generatedId;
      if (!gid) {
        // Create on first save
        const r = await api.post(`/admin/crm/customer/${customerId}/deal-jacket/generate`,
          { template_id: templateId, field_values: values, save: true });
        gid = r.data.generated_id;
        setGeneratedId(gid);
      } else {
        await api.put(`/admin/crm/customer/${customerId}/deal-jacket/generated/${gid}`,
          { field_values: values, update_customer: !!updateCustomer, status: "Edited" });
      }
      // Recompute missing
      const m = []; const rm = [];
      (template.fields || []).forEach(f => {
        const v = values[f.id];
        if (v === undefined || v === null || v === "") {
          const entry = { id: f.id, label: f.label, required: !!f.required };
          m.push(entry); if (f.required) rm.push(entry);
        }
      });
      setMissing(m); setRequiredMissing(rm);
      setStatusMsg("Saved · " + new Date().toLocaleTimeString());
    } catch (e) {
      setStatusMsg("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const doPrint = async () => {
    if (requiredMissing.length > 0) {
      const msg = "Required fields are missing:\n• " + requiredMissing.map(r => r.label).join("\n• ") +
                  "\n\nFill them in, mark them not applicable, or remove the required setting from the template.";
      alert(msg);
      return;
    }
    if (missing.length > 0) {
      const ok = window.confirm(`${missing.length} optional field${missing.length === 1 ? "" : "s"} ${missing.length === 1 ? "is" : "are"} blank:\n• ${missing.map(r => r.label).join("\n• ")}\n\nPrint anyway?`);
      if (!ok) return;
    }
    // Make sure the doc is saved first (so the print is logged against a generated_id)
    if (!generatedId) await save(false);
    // Log
    if (generatedId) {
      api.post(`/admin/crm/customer/${customerId}/deal-jacket/generated/${generatedId}/log`,
        { action: "Printed" }).catch(() => {});
    }
    window.print();
  };

  if (!template) {
    return <div className="ssc-container ssc-section" data-testid="form-view-loading">Loading form…</div>;
  }

  return (
    <div className="form-view-root" data-testid="form-view">
      <style>{`
        @media print {
          body { background: #fff !important; }
          .no-print, header, footer, nav { display: none !important; }
          .form-page { box-shadow: none !important; margin: 0 !important; page-break-after: always; }
          .form-page:last-child { page-break-after: auto; }
          .form-field-input { border: none !important; outline: none !important; background: transparent !important; }
        }
        .form-page { background: #fff; box-shadow: 0 2px 12px rgba(0,0,0,.08); margin: 20px auto; position: relative; }
        .form-field-input { width: 100%; height: 100%; border: 1px dashed #94a3b8; outline: none; background: rgba(255,255,255,.7); padding: 2px 4px; box-sizing: border-box; }
        .form-field-input:focus { border-color: #006DFF; background: #fff; }
        .form-field-input.notes { resize: none; }
      `}</style>

      <div className="ssc-container no-print" style={{ paddingTop: 16, paddingBottom: 12 }}>
        <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
          <div>
            <Link to={`/admin/crm/customer/${customerId}`} className="text-xs text-blue-600 hover:underline">← Back to Deal Jacket</Link>
            <h1 className="text-xl font-bold text-[#061426] mt-1" data-testid="form-view-title">{template.name} <span className="text-sm font-normal text-gray-500">v{template.version}</span></h1>
            {generated && generated.template_version !== template.version && (
              <div className="text-xs text-amber-700 mt-1">⚠ This saved document was generated against v{generated.template_version}; current template is v{template.version}.</div>
            )}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {statusMsg && <span className="text-xs text-gray-500">{statusMsg}</span>}
            <button className="ssc-btn-secondary" onClick={() => save(false)} disabled={saving} data-testid="form-save">{saving ? "Saving…" : "Save to Customer File"}</button>
            <button className="ssc-btn-secondary" onClick={() => save(true)} disabled={saving} data-testid="form-save-update">Save + Update Customer Profile</button>
            <button className="ssc-btn-primary" onClick={doPrint} data-testid="form-print">Print This Form</button>
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-300 rounded p-2 text-sm">
          <strong>Please review</strong> all customer, vehicle, trade, insurance, payoff, and deal information for accuracy before printing. Auto-filled information may be incomplete or incorrect.
        </div>
        {missing.length > 0 && (
          <div className="text-xs text-gray-600 mt-2" data-testid="form-missing-summary">
            <span className={requiredMissing.length ? "text-red-700 font-semibold" : ""}>
              {missing.length} blank field{missing.length === 1 ? "" : "s"}
              {requiredMissing.length ? ` · ${requiredMissing.length} required` : ""}
            </span>
            <span className="text-gray-500"> — {missing.slice(0, 6).map(m => m.label).join(" · ")}{missing.length > 6 ? ` · +${missing.length - 6} more` : ""}</span>
          </div>
        )}
      </div>

      <div ref={printRootRef} data-testid="form-print-root">
        {(template.pages || []).map(p => (
          <PageOverlay key={p.page} page={p} bgUrl={pageUrls[p.page]}
            fields={(template.fields || []).filter(f => (f.page || 1) === p.page)}
            values={values} setValue={setValue} />
        ))}
      </div>
    </div>
  );
}

function PageOverlay({ page, bgUrl, fields, values, setValue }) {
  // Render at fixed CSS pixel width 850 (standard 8.5"x11" approx at 100dpi).
  const cssWidth = 850;
  const cssHeight = page.width ? Math.round(cssWidth * (page.height / page.width)) : 1100;
  return (
    <div className="form-page" style={{ width: cssWidth, height: cssHeight, position: "relative" }}
      data-testid={`form-page-${page.page}`}>
      {bgUrl && <img src={bgUrl} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "fill" }} draggable={false} />}
      {fields.map(f => (
        <FieldBox key={f.id} f={f} value={values[f.id] ?? ""} onChange={(v) => setValue(f.id, v)} />
      ))}
    </div>
  );
}

function FieldBox({ f, value, onChange }) {
  const style = {
    position: "absolute",
    left: `${f.x * 100}%`, top: `${f.y * 100}%`,
    width: `${f.w * 100}%`, height: `${f.h * 100}%`,
    fontSize: (f.font_size || 12) + "px",
    textAlign: f.alignment || "left",
  };
  const test = `field-input-${f.id}`;
  if (f.type === "checkbox") {
    return (
      <label style={style} className="flex items-center justify-center">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={test} />
      </label>
    );
  }
  if (f.type === "notes") {
    return <textarea className="form-field-input notes" style={style} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={test} />;
  }
  if (f.type === "dropdown") {
    return (
      <select className="form-field-input" style={style} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={test}>
        <option value=""></option>
        {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (f.type === "signature" || f.type === "initials") {
    return (
      <div style={{ ...style, borderBottom: "1px solid #061426" }} className="flex items-end px-1 text-xs text-gray-400 no-print-empty">
        {value || (f.type === "signature" ? "Signature" : "Initials")}
      </div>
    );
  }
  if (f.type === "date") {
    // HTML5 date inputs need yyyy-mm-dd. Accept MM/DD/YYYY from the resolver and
    // normalize for the input, then convert back to the printable MM/DD/YYYY on save.
    const toISO = (s) => {
      if (!s) return "";
      const m = String(s).match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
      if (m) return `${m[3]}-${String(m[1]).padStart(2, "0")}-${String(m[2]).padStart(2, "0")}`;
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
      return "";
    };
    const fromISO = (iso) => {
      if (!iso) return "";
      const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      return m ? `${m[2]}/${m[3]}/${m[1]}` : iso;
    };
    return (
      <input className="form-field-input" type="date" style={style}
        value={toISO(value)} onChange={(e) => onChange(fromISO(e.target.value))} data-testid={test} />
    );
  }
  const inputType = f.type === "number" || f.type === "currency" ? "number" : f.type === "email" ? "email" : "text";
  return (
    <input className="form-field-input" type={inputType} style={style} value={value || ""}
      onChange={(e) => onChange(e.target.value)} data-testid={test} />
  );
}
